import { createFileRoute } from "@tanstack/react-router";
import { Space, Table, Typography } from "antd";
import { useEffect } from "react";
import { useOrganizationMetrics } from "@/common/artemis-operations/organization";
import { usePlatformFeatures } from "@/common/artemis-operations/platform-features";
import {
	BaseContainer,
	BasePageContainer,
	QueryGuard,
} from "@/components/base";
import { OrganizationBilling } from "@/components/workspaces/organization-billing";
import { usePageTitle } from "@/contexts/page-title-context";

export const Route = createFileRoute("/_authenticated/$orgSlug/admin/billing")({
	component: AdminBillingPage,
});

const fmtInt = (n: number) => n.toLocaleString();

function QuotaSection() {
	const { data: metrics, isPending, isError } = useOrganizationMetrics();

	// Used counts are what enforcement compares against the cap (active-only for
	// users/agents); a null cap means unlimited.
	const quotas = metrics && [
		{
			key: "users",
			resource: "Active users",
			used: metrics.userCount,
			quota: metrics.maxActiveUsers,
		},
		{
			key: "workspaces",
			resource: "Workspaces",
			used: metrics.workspaceCount,
			quota: metrics.maxWorkspaces,
		},
		{
			key: "agents",
			resource: "Agents",
			used: metrics.agentCount,
			quota: metrics.maxAgents,
		},
		{
			key: "environments",
			resource: "Environments",
			used: metrics.environmentCount,
			quota: metrics.maxEnvironments,
		},
	];

	return (
		<QueryGuard isPending={isPending} isError={isError} isNull={!metrics}>
			<Space vertical style={{ width: "100%" }}>
				<BaseContainer title="Quotas">
					{quotas && (
						<Table
							size="small"
							pagination={false}
							rowKey="key"
							dataSource={quotas}
							columns={[
								{ title: "Resource", dataIndex: "resource" },
								{
									title: "In use",
									dataIndex: "used",
									// Red once usage exceeds the cap (e.g. after a downgrade); null cap = unlimited.
									render: (v: number, row: { quota: number | null }) => (
										<Typography.Text
											type={
												row.quota != null && v > row.quota
													? "danger"
													: "success"
											}
										>
											{fmtInt(v)}
										</Typography.Text>
									),
								},
								{
									title: "Quota",
									dataIndex: "quota",
									render: (v: number | null) =>
										v != null ? fmtInt(v) : "Unlimited",
								},
							]}
						/>
					)}
				</BaseContainer>
			</Space>
		</QueryGuard>
	);
}

/**
 * Org-admin Billing: the plan (Stripe, ARCH-034, and/or AWS Marketplace — only when enabled), then what the org
 * uses against its quotas. A paid plan's environment count is the Environments quota below. The
 * usage counters live on the Organization page.
 */
function AdminBillingPage() {
	const { setTitle } = usePageTitle();
	useEffect(() => setTitle("Billing"), [setTitle]);
	const { data: features } = usePlatformFeatures();

	return (
		<BasePageContainer>
			{(features?.stripeBilling || features?.awsMarketplace) && (
				<OrganizationBilling stripeEnabled={features.stripeBilling} />
			)}
			<QuotaSection />
		</BasePageContainer>
	);
}
